import { useMemo, useState } from "react";
import { EXAMS } from "../data/exams";
import { glossaryFor } from "../data/localized";
import type { ExamId } from "../data/types";
import { GuideTabs } from "../components/GuideTabs";
import { useDocumentTitle } from "../components/ui";
import { useLang, useT } from "../i18n";

const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase();

export function GlossaryPage() {
  const t = useT();
  const lang = useLang();
  useDocumentTitle(t("Thuật ngữ", "Glossary"));
  const [q, setQ] = useState("");
  const [exam, setExam] = useState<ExamId | "all">("all");
  const terms = glossaryFor(lang);
  const list = useMemo(() => {
    const needle = norm(q.trim());
    return terms
      .filter((x) => exam === "all" || x.exam === exam)
      .filter((x) => !needle || norm(`${x.term} ${x.vi} ${x.def}`).includes(needle))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [terms, q, exam]);

  return (
    <div className="reading-page glossary-page">
      <h1>{t("Thuật ngữ", "Glossary")}</h1>
      <p className="muted">
        {t(
          `${terms.length} thuật ngữ giữ nguyên tiếng Anh như trong đề thi, kèm nghĩa tiếng Việt và giải thích ngắn.`,
          `${terms.length} terms exactly as they appear on the exam, with short definitions.`,
        )}
      </p>
      <GuideTabs />
      <div className="card stack">
        <input
          type="search"
          className="search"
          placeholder={t("Tìm thuật ngữ…", "Search terms…")}
          aria-label={t("Tìm thuật ngữ", "Search terms")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="pill-group" role="group" aria-label={t("Lọc theo chứng chỉ", "Filter by certification")}>
          <button type="button" className="pill" aria-pressed={exam === "all"} onClick={() => setExam("all")}>{t("Tất cả", "All")}</button>
          {EXAMS.map((e) => (
            <button key={e.id} type="button" className="pill" aria-pressed={exam === e.id} onClick={() => setExam(e.id)}>{e.code}</button>
          ))}
        </div>
      </div>
      <p className="small muted mt" role="status">{t(`${list.length} kết quả`, `${list.length} results`)}</p>
      <div className="term-list">
        {list.map((x) => (
          <div key={x.term} className="card">
            <div className="row between">
              <strong lang="en">{x.term}</strong>
              <span className="chip small">{EXAMS.find((e) => e.id === x.exam)?.code}</span>
            </div>
            {lang === "vi" && <div className="small muted">{x.vi}</div>}
            <p className="small mb-0 mt">{x.def}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
